// src/components/settings/RecycleBin.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, Table, TableRow, Chip } from '@mui/material';
import {
  recycleBinAPI,
  DeletedClient,
  DeletedPet,
  DeletedBooking,
  DeletedUser,
  Vet,
  PetType,
  PetSize,
  PetBreed,
  PetColour,
  KennelSize,
  KennelSide,
} from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useGlobalToast } from '../../App';
import { useTabQueryParam } from '../../hooks/useTabQueryParam';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import SettingsPageRecycleBinSubMenu from '../menus/SettingsPageRecycleBinSubMenu';
import RecycleBinActions from './shared/RecycleBinActions';
import SimpleRecycleBinTable, {
  SimpleDeletedItem,
} from './shared/SimpleRecycleBinTable';
import { DeletedChip } from './shared/SettingsElements';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableChipCell,
  StyledTableActionsCell,
} from './shared/SettingsTableElements';
import RecycleBinConfirmDialog from './shared/dialogs/RecycleBinConfirmDialog';
import LoadingSpinner from '../common/LoadingSpinner';

interface ConfirmDialog {
  open: boolean;
  title: string;
  message: string;
  variant: 'restore' | 'delete';
  onConfirm: () => void;
}

const EMPTY_DIALOG: ConfirmDialog = {
  open: false,
  title: '',
  message: '',
  variant: 'restore',
  onConfirm: () => {},
};

const formatDeletedDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const RECYCLE_SUB_TAB_IDS = [
  'clients',
  'pets',
  'bookings',
  'users',
  'vets',
  'petTypes',
  'petSizes',
  'petBreeds',
  'petColours',
  'kennelSizes',
  'kennelSides',
] as const;
type RecycleSubTabId = (typeof RECYCLE_SUB_TAB_IDS)[number];

const recycleSubTabIdToIndex = (id: RecycleSubTabId): number =>
  RECYCLE_SUB_TAB_IDS.indexOf(id);
const recycleSubTabIndexToId = (index: number): RecycleSubTabId =>
  RECYCLE_SUB_TAB_IDS[index] ?? RECYCLE_SUB_TAB_IDS[0];

const formatBookingDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Ordered 1:1 with the Promise.allSettled array inside fetchDeleted - used
// to build a human-readable list of which categories failed, if any did.
// Module-level rather than inside the component: it's static, so there's
// no reason to recreate it every render or carry it as a useCallback dep.
const FETCH_CATEGORY_LABELS = [
  'Clients',
  'Pets',
  'Bookings',
  'Users',
  'Vets',
  'Pet Types',
  'Pet Sizes',
  'Pet Breeds',
  'Pet Colours',
  'Kennel Sizes',
  'Kennel Sides',
] as const;

const RecycleBin: React.FC = () => {
  const [subTabId, setSubTabId] = useTabQueryParam<RecycleSubTabId>(
    'recycleSubTab',
    RECYCLE_SUB_TAB_IDS,
    'clients'
  );
  const activeTab = recycleSubTabIdToIndex(subTabId);
  const setActiveTab = (index: number) =>
    setSubTabId(recycleSubTabIndexToId(index));
  const [clients, setClients] = useState<DeletedClient[]>([]);
  const [pets, setPets] = useState<DeletedPet[]>([]);
  const [bookings, setBookings] = useState<DeletedBooking[]>([]);
  const [users, setUsers] = useState<DeletedUser[]>([]);
  const [vets, setVets] = useState<Vet[]>([]);
  const [petTypes, setPetTypes] = useState<PetType[]>([]);
  const [petSizes, setPetSizes] = useState<PetSize[]>([]);
  const [petBreeds, setPetBreeds] = useState<PetBreed[]>([]);
  const [petColours, setPetColours] = useState<PetColour[]>([]);
  const [kennelSizes, setKennelSizes] = useState<KennelSize[]>([]);
  const [kennelSides, setKennelSides] = useState<KennelSide[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [dialog, setDialog] = useState<ConfirmDialog>(EMPTY_DIALOG);
  const [searchText, setSearchText] = useState('');
  const { handleError } = useErrorHandler();
  const { showSuccess } = useGlobalToast();
  const { canManageUsers } = usePermissions();
  const { isLoading: authLoading } = useAuth();

  useEffect(() => {
    setSearchText('');
  }, [activeTab]);

  const fetchDeleted = useCallback(async () => {
    setLoading(true);
    try {
      // Promise.allSettled, not Promise.all: a single category's failure
      // (e.g. a transient 500, or a role-gated 403 for a category this
      // user genuinely can't see) used to reject the whole batch and blank
      // every other category too - this is exactly what happened with the
      // Users/Manager bug fixed above, just generalized. Each category now
      // resolves or fails independently; a failed one falls back to an
      // empty list instead of taking the other ten down with it.
      const results = await Promise.allSettled([
        recycleBinAPI.getDeletedClients(),
        recycleBinAPI.getDeletedPets(),
        recycleBinAPI.getDeletedBookings(),
        // Deleted Users is Admin-only on the backend (UserController is
        // class-level [Authorize(Roles = "Admin")]) - fetching it as a
        // Manager 403s. Managers simply never see any deleted Users,
        // matching the fact that they can't manage Users anywhere else in
        // the app either.
        canManageUsers ? recycleBinAPI.getDeletedUsers() : Promise.resolve([]),
        recycleBinAPI.getDeletedVets(),
        recycleBinAPI.getDeletedPetTypes(),
        recycleBinAPI.getDeletedPetSizes(),
        recycleBinAPI.getDeletedPetBreeds(),
        recycleBinAPI.getDeletedPetColours(),
        recycleBinAPI.getDeletedKennelSizes(),
        recycleBinAPI.getDeletedKennelSides(),
      ]);
      const [c, p, b, u, v, pt, ps, pb, pc, ks, kd] = results;

      setClients(c.status === 'fulfilled' ? c.value : []);
      setPets(p.status === 'fulfilled' ? p.value : []);
      setBookings(b.status === 'fulfilled' ? b.value : []);
      setUsers(u.status === 'fulfilled' ? u.value : []);
      setVets(v.status === 'fulfilled' ? v.value : []);
      setPetTypes(pt.status === 'fulfilled' ? pt.value : []);
      setPetSizes(ps.status === 'fulfilled' ? ps.value : []);
      setPetBreeds(pb.status === 'fulfilled' ? pb.value : []);
      setPetColours(pc.status === 'fulfilled' ? pc.value : []);
      setKennelSizes(ks.status === 'fulfilled' ? ks.value : []);
      setKennelSides(kd.status === 'fulfilled' ? kd.value : []);

      const failedLabels = results
        .map((r, i) =>
          r.status === 'rejected' ? FETCH_CATEGORY_LABELS[i] : null
        )
        .filter(
          (label): label is (typeof FETCH_CATEGORY_LABELS)[number] =>
            label !== null
        );

      if (failedLabels.length > 0) {
        handleError(new Error(`Failed to load: ${failedLabels.join(', ')}`), {
          context: 'loading recycle bin',
        });
      }
    } catch (err) {
      // Defensive fallback only - Promise.allSettled itself never rejects,
      // so this only fires on a genuinely unexpected synchronous error
      // (e.g. a bug in the state-setting logic above), not a fetch
      // failure - those are all handled per-category above.
      handleError(err, { context: 'loading recycle bin' });
    } finally {
      setLoading(false);
    }
  }, [handleError, canManageUsers]);

  useEffect(() => {
    // AuthContext resolves the real user one render after mount (it reads
    // localStorage inside its own useEffect, not a lazy useState
    // initializer) - canManageUsers would otherwise flip false -> true
    // right after the first fetch, and since fetchDeleted's identity
    // depends on it, that would silently trigger a second, redundant
    // full fetch of all eleven categories. Waiting for authLoading to
    // settle means fetchDeleted only ever runs once, already holding the
    // correct final value.
    if (authLoading) return;
    fetchDeleted();
  }, [fetchDeleted, authLoading]);

  const filteredClients = useMemo(() => {
    if (!searchText) return clients;
    const lower = searchText.toLowerCase();
    return clients.filter((c) =>
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(lower)
    );
  }, [clients, searchText]);

  const filteredPets = useMemo(() => {
    if (!searchText) return pets;
    const lower = searchText.toLowerCase();
    return pets.filter(
      (p) =>
        p.name.toLowerCase().includes(lower) ||
        p.petType?.toLowerCase().includes(lower) ||
        p.clientName?.toLowerCase().includes(lower)
    );
  }, [pets, searchText]);

  const filteredBookings = useMemo(() => {
    if (!searchText) return bookings;
    const lower = searchText.toLowerCase();
    return bookings.filter(
      (b) =>
        `#${b.id}`.includes(lower) ||
        b.clientName?.toLowerCase().includes(lower) ||
        b.status?.toLowerCase().includes(lower)
    );
  }, [bookings, searchText]);

  const filteredUsers = useMemo(() => {
    if (!searchText) return users;
    const lower = searchText.toLowerCase();
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(lower) ||
        u.email?.toLowerCase().includes(lower) ||
        u.role?.toLowerCase().includes(lower)
    );
  }, [users, searchText]);

  // Normalization + search filtering for the 7 "simple" entity types
  // (Vets, Pet Types/Sizes/Breeds/Colours, Kennel Sizes/Sides), each
  // rendered via the shared SimpleRecycleBinTable rather than bespoke
  // markup - see clients/pets/bookings/users above for the richer,
  // bespoke-table entities.
  const filterSimpleItems = (
    items: SimpleDeletedItem[],
    text: string
  ): SimpleDeletedItem[] => {
    if (!text) return items;
    const lower = text.toLowerCase();
    return items.filter(
      (i) =>
        i.name.toLowerCase().includes(lower) ||
        i.subLabel?.toLowerCase().includes(lower)
    );
  };

  const vetItems: SimpleDeletedItem[] = useMemo(
    () =>
      vets.map((v) => ({
        id: v.id,
        name: v.name,
        subLabel: v.phone,
        deletedAt: v.deletedAt ?? '',
      })),
    [vets]
  );
  const filteredVetItems = useMemo(
    () => filterSimpleItems(vetItems, searchText),
    [vetItems, searchText]
  );

  const petTypeItems: SimpleDeletedItem[] = useMemo(
    () =>
      petTypes.map((pt) => ({
        id: pt.id,
        name: pt.type,
        deletedAt: pt.deletedAt ?? '',
      })),
    [petTypes]
  );
  const filteredPetTypeItems = useMemo(
    () => filterSimpleItems(petTypeItems, searchText),
    [petTypeItems, searchText]
  );

  const petSizeItems: SimpleDeletedItem[] = useMemo(
    () =>
      petSizes.map((ps) => ({
        id: ps.id,
        name: ps.size,
        subLabel: ps.petType?.type,
        deletedAt: ps.deletedAt ?? '',
      })),
    [petSizes]
  );
  const filteredPetSizeItems = useMemo(
    () => filterSimpleItems(petSizeItems, searchText),
    [petSizeItems, searchText]
  );

  const petBreedItems: SimpleDeletedItem[] = useMemo(
    () =>
      petBreeds.map((pb) => ({
        id: pb.id,
        name: pb.breed,
        subLabel: pb.petTypeNavigation?.type,
        deletedAt: pb.deletedAt ?? '',
      })),
    [petBreeds]
  );
  const filteredPetBreedItems = useMemo(
    () => filterSimpleItems(petBreedItems, searchText),
    [petBreedItems, searchText]
  );

  const petColourItems: SimpleDeletedItem[] = useMemo(
    () =>
      petColours.map((pc) => ({
        id: pc.id,
        name: pc.colour,
        subLabel: pc.petTypeNavigation?.type,
        deletedAt: pc.deletedAt ?? '',
      })),
    [petColours]
  );
  const filteredPetColourItems = useMemo(
    () => filterSimpleItems(petColourItems, searchText),
    [petColourItems, searchText]
  );

  const kennelSizeItems: SimpleDeletedItem[] = useMemo(
    () =>
      kennelSizes.map((ks) => ({
        id: ks.id,
        name: ks.name,
        deletedAt: ks.deletedAt ?? '',
      })),
    [kennelSizes]
  );
  const filteredKennelSizeItems = useMemo(
    () => filterSimpleItems(kennelSizeItems, searchText),
    [kennelSizeItems, searchText]
  );

  const kennelSideItems: SimpleDeletedItem[] = useMemo(
    () =>
      kennelSides.map((kd) => ({
        id: kd.id,
        name: kd.name,
        deletedAt: kd.deletedAt ?? '',
      })),
    [kennelSides]
  );
  const filteredKennelSideItems = useMemo(
    () => filterSimpleItems(kennelSideItems, searchText),
    [kennelSideItems, searchText]
  );

  const recycleBinTabs = useMemo(
    () => [
      { label: `Clients (${clients.length})`, minWidth: '160px' },
      { label: `Pets (${pets.length})`, minWidth: '120px' },
      { label: `Bookings (${bookings.length})`, minWidth: '190px' },
      { label: `Users (${users.length})`, minWidth: '150px' },
      { label: `Vets (${vets.length})`, minWidth: '140px' },
      { label: `Pet Types (${petTypes.length})`, minWidth: '170px' },
      { label: `Pet Sizes (${petSizes.length})`, minWidth: '170px' },
      { label: `Pet Breeds (${petBreeds.length})`, minWidth: '180px' },
      { label: `Pet Colours (${petColours.length})`, minWidth: '190px' },
      { label: `Kennel Sizes (${kennelSizes.length})`, minWidth: '200px' },
      { label: `Kennel Sides (${kennelSides.length})`, minWidth: '200px' },
    ],
    [
      clients.length,
      pets.length,
      bookings.length,
      users.length,
      vets.length,
      petTypes.length,
      petSizes.length,
      petBreeds.length,
      petColours.length,
      kennelSizes.length,
      kennelSides.length,
    ]
  );

  const closeDialog = () => setDialog(EMPTY_DIALOG);

  const runAction = async (
    key: string,
    action: () => Promise<void>,
    successMsg: string
  ) => {
    setActionInProgress(key);
    try {
      await action();
      showSuccess(successMsg);
      await fetchDeleted();
    } catch (err) {
      handleError(err, { context: successMsg.toLowerCase() });
    } finally {
      setActionInProgress(null);
    }
  };

  const confirmRestore = (type: string, name: string, id: number) => {
    const cascadeNote =
      type === 'client'
        ? ' This will also restore all their pets, bookings, and invoices.'
        : type === 'booking'
          ? ' This will also restore related invoices and payments.'
          : '';
    setDialog({
      open: true,
      title: `Restore ${type}?`,
      message: `Restore "${name}"?${cascadeNote}`,
      variant: 'restore',
      onConfirm: () => {
        closeDialog();
        const actions: Record<string, () => Promise<void>> = {
          client: () => recycleBinAPI.restoreClient(id),
          pet: () => recycleBinAPI.restorePet(id),
          booking: () => recycleBinAPI.restoreBooking(id),
          user: () => recycleBinAPI.restoreUser(id),
          vet: () => recycleBinAPI.restoreVet(id),
          'pet type': () => recycleBinAPI.restorePetType(id),
          'pet size': () => recycleBinAPI.restorePetSize(id),
          'pet breed': () => recycleBinAPI.restorePetBreed(id),
          'pet colour': () => recycleBinAPI.restorePetColour(id),
          'kennel size': () => recycleBinAPI.restoreKennelSize(id),
          'kennel side': () => recycleBinAPI.restoreKennelSide(id),
        };
        runAction(
          `restore-${id}`,
          actions[type],
          `${type.charAt(0).toUpperCase() + type.slice(1)} restored`
        );
      },
    });
  };

  const confirmPermanentDelete = (type: string, name: string, id: number) => {
    const cascadeNote =
      type === 'client'
        ? " Their pets and bookings will be removed too. A client with invoices or payments on record can't be permanently deleted."
        : type === 'booking'
          ? " A booking with an invoice on record can't be permanently deleted."
          : type === 'pet'
            ? " A pet with booking history can't be permanently deleted."
            : '';
    setDialog({
      open: true,
      title: `Permanently delete ${type}?`,
      message: `This will permanently delete "${name}" and cannot be undone.${cascadeNote}`,
      variant: 'delete',
      onConfirm: () => {
        closeDialog();
        const actions: Record<string, () => Promise<void>> = {
          client: () => recycleBinAPI.permanentDeleteClient(id),
          pet: () => recycleBinAPI.permanentDeletePet(id),
          booking: () => recycleBinAPI.permanentDeleteBooking(id),
          user: () => recycleBinAPI.permanentDeleteUser(id),
          vet: () => recycleBinAPI.permanentDeleteVet(id),
          'pet type': () => recycleBinAPI.permanentDeletePetType(id),
          'pet size': () => recycleBinAPI.permanentDeletePetSize(id),
          'pet breed': () => recycleBinAPI.permanentDeletePetBreed(id),
          'pet colour': () => recycleBinAPI.permanentDeletePetColour(id),
          'kennel size': () => recycleBinAPI.permanentDeleteKennelSize(id),
          'kennel side': () => recycleBinAPI.permanentDeleteKennelSide(id),
        };
        runAction(
          `delete-${id}`,
          actions[type],
          `${type.charAt(0).toUpperCase() + type.slice(1)} permanently deleted`
        );
      },
    });
  };

  const totalCount =
    clients.length +
    pets.length +
    bookings.length +
    users.length +
    vets.length +
    petTypes.length +
    petSizes.length +
    petBreeds.length +
    petColours.length +
    kennelSizes.length +
    kennelSides.length;
  const busy = actionInProgress !== null;

  const searchPlaceholders: Record<number, string> = {
    0: 'Search deleted clients...',
    1: 'Search deleted pets...',
    2: 'Search deleted bookings...',
    3: 'Search deleted users...',
    4: 'Search deleted vets...',
    5: 'Search deleted pet types...',
    6: 'Search deleted pet sizes...',
    7: 'Search deleted pet breeds...',
    8: 'Search deleted pet colours...',
    9: 'Search deleted kennel sizes...',
    10: 'Search deleted kennel sides...',
  };

  if (loading) {
    return <LoadingSpinner message="Loading Recycle Bin..." size={80} />;
  }

  return (
    <Box>
      <SettingsPageRecycleBinSubMenu
        tabs={recycleBinTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {activeTab === 0 && (
        <>
          <SettingsHeaderRow
            title="Clients"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[0]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          {clients.length === 0 ? (
            <Alert severity="info">No deleted clients.</Alert>
          ) : filteredClients.length === 0 ? (
            <Alert severity="info">
              No deleted clients match "{searchText}".
            </Alert>
          ) : (
            <StyledTableContainer>
              <Table size="small">
                <StyledTableHead>
                  <TableRow>
                    <StyledTableHeaderCell>Name</StyledTableHeaderCell>
                    <StyledTableHeaderCell align="center">
                      Pets
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell align="center">
                      Bookings
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell>Deleted</StyledTableHeaderCell>
                    <StyledTableHeaderCell align="right">
                      Actions
                    </StyledTableHeaderCell>
                  </TableRow>
                </StyledTableHead>
                <StyledTableBody>
                  {filteredClients.map((client, index) => {
                    const name = `${client.firstName} ${client.lastName}`;
                    return (
                      <StyledTableRow key={client.id} isOdd={index % 2 === 0}>
                        <StyledTableCell>
                          <Typography>{name}</Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Typography>{client.petCount}</Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Typography>{client.bookingCount}</Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography>
                            {formatDeletedDate(client.deletedAt)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableActionsCell align="right">
                          <RecycleBinActions
                            itemId={client.id}
                            itemName={name}
                            onRestore={() =>
                              confirmRestore('client', name, client.id)
                            }
                            onDelete={() =>
                              confirmPermanentDelete('client', name, client.id)
                            }
                            actionInProgress={actionInProgress}
                            busy={busy}
                            restoreTooltip="Restore client and all related data"
                            deleteTooltip="Permanently delete"
                          />
                        </StyledTableActionsCell>
                      </StyledTableRow>
                    );
                  })}
                </StyledTableBody>
              </Table>
            </StyledTableContainer>
          )}
        </>
      )}

      {activeTab === 1 && (
        <>
          <SettingsHeaderRow
            title="Pets"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[1]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          {pets.length === 0 ? (
            <Alert severity="info">No deleted pets.</Alert>
          ) : filteredPets.length === 0 ? (
            <Alert severity="info">No deleted pets match "{searchText}".</Alert>
          ) : (
            <StyledTableContainer>
              <Table size="small">
                <StyledTableHead>
                  <TableRow>
                    <StyledTableHeaderCell>Name</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Type</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Owner</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Deleted</StyledTableHeaderCell>
                    <StyledTableHeaderCell align="right">
                      Actions
                    </StyledTableHeaderCell>
                  </TableRow>
                </StyledTableHead>
                <StyledTableBody>
                  {filteredPets.map((pet, index) => (
                    <StyledTableRow key={pet.id} isOdd={index % 2 === 0}>
                      <StyledTableCell>
                        <Typography>{pet.name}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography>{pet.petType}</Typography>
                      </StyledTableCell>
                      <StyledTableChipCell
                        sx={{
                          whiteSpace: 'nowrap',
                          fontSize: '1.8rem',
                        }}
                      >
                        <Box
                          sx={{
                            gap: 1,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{ display: 'inline-block', fontSize: '1.8rem' }}
                          >
                            {pet.clientName}
                          </Typography>
                          {pet.clientIsDeleted && <DeletedChip />}
                        </Box>
                      </StyledTableChipCell>
                      <StyledTableCell>
                        <Typography>
                          {formatDeletedDate(pet.deletedAt)}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableActionsCell align="right">
                        <RecycleBinActions
                          itemId={pet.id}
                          itemName={pet.name}
                          onRestore={() =>
                            confirmRestore('pet', pet.name, pet.id)
                          }
                          onDelete={() =>
                            confirmPermanentDelete('pet', pet.name, pet.id)
                          }
                          actionInProgress={actionInProgress}
                          busy={busy}
                          disabled={pet.clientIsDeleted}
                          restoreTooltip={
                            pet.clientIsDeleted
                              ? 'Restore the owner first'
                              : 'Restore pet'
                          }
                          deleteTooltip={
                            pet.clientIsDeleted
                              ? 'Delete the owner to remove all related data'
                              : 'Permanently delete'
                          }
                        />
                      </StyledTableActionsCell>
                    </StyledTableRow>
                  ))}
                </StyledTableBody>
              </Table>
            </StyledTableContainer>
          )}
        </>
      )}

      {activeTab === 2 && (
        <>
          <SettingsHeaderRow
            title="Bookings"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[2]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          {bookings.length === 0 ? (
            <Alert severity="info">No deleted bookings.</Alert>
          ) : filteredBookings.length === 0 ? (
            <Alert severity="info">
              No deleted bookings match "{searchText}".
            </Alert>
          ) : (
            <StyledTableContainer>
              <Table size="small">
                <StyledTableHead>
                  <TableRow>
                    <StyledTableHeaderCell>Booking #</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Client</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Check-in</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Check-out</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Status</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Deleted</StyledTableHeaderCell>
                    <StyledTableHeaderCell align="right">
                      Actions
                    </StyledTableHeaderCell>
                  </TableRow>
                </StyledTableHead>
                <StyledTableBody>
                  {filteredBookings.map((booking, index) => {
                    const name = `Booking #${booking.id}`;
                    return (
                      <StyledTableRow key={booking.id} isOdd={index % 2 === 0}>
                        <StyledTableCell sx={{ fontWeight: 600 }}>
                          <Typography>#{booking.id}</Typography>
                        </StyledTableCell>
                        <StyledTableChipCell>
                          <Box
                            sx={{
                              gap: 1,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                display: 'inline-block',
                                fontSize: '1.8rem',
                              }}
                            >
                              {booking.clientName}
                            </Typography>
                            {booking.clientIsDeleted && <DeletedChip />}
                          </Box>
                        </StyledTableChipCell>
                        <StyledTableCell>
                          <Typography>
                            {formatBookingDate(booking.checkInDate)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography>
                            {formatBookingDate(booking.checkOutDate)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography>{booking.status}</Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography>
                            {formatDeletedDate(booking.deletedAt)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableActionsCell align="right">
                          <RecycleBinActions
                            itemId={booking.id}
                            itemName={name}
                            onRestore={() =>
                              confirmRestore('booking', name, booking.id)
                            }
                            onDelete={() =>
                              confirmPermanentDelete(
                                'booking',
                                name,
                                booking.id
                              )
                            }
                            actionInProgress={actionInProgress}
                            busy={busy}
                            disabled={booking.clientIsDeleted}
                            restoreTooltip={
                              booking.clientIsDeleted
                                ? 'Restore the client first'
                                : 'Restore booking and invoices'
                            }
                            deleteTooltip={
                              booking.clientIsDeleted
                                ? 'Delete the client to remove all related data'
                                : 'Permanently delete'
                            }
                          />
                        </StyledTableActionsCell>
                      </StyledTableRow>
                    );
                  })}
                </StyledTableBody>
              </Table>
            </StyledTableContainer>
          )}
        </>
      )}

      {activeTab === 3 && (
        <>
          <SettingsHeaderRow
            title="Users"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[3]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          {users.length === 0 ? (
            <Alert severity="info">No deleted users.</Alert>
          ) : filteredUsers.length === 0 ? (
            <Alert severity="info">
              No deleted users match "{searchText}".
            </Alert>
          ) : (
            <StyledTableContainer>
              <Table size="small">
                <StyledTableHead>
                  <TableRow>
                    <StyledTableHeaderCell>Username</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Email</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Role</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Deleted</StyledTableHeaderCell>
                    <StyledTableHeaderCell align="right">
                      Actions
                    </StyledTableHeaderCell>
                  </TableRow>
                </StyledTableHead>
                <StyledTableBody>
                  {filteredUsers.map((user, index) => (
                    <StyledTableRow key={user.id} isOdd={index % 2 === 0}>
                      <StyledTableCell>
                        <Typography>{user.username}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography>{user.email}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography>{user.role}</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography>
                          {formatDeletedDate(user.deletedAt)}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableActionsCell align="right">
                        <RecycleBinActions
                          itemId={user.id}
                          itemName={user.username}
                          onRestore={() =>
                            confirmRestore('user', user.username, user.id)
                          }
                          onDelete={() =>
                            confirmPermanentDelete(
                              'user',
                              user.username,
                              user.id
                            )
                          }
                          actionInProgress={actionInProgress}
                          busy={busy}
                          restoreTooltip="Restore user"
                          deleteTooltip="Permanently delete"
                        />
                      </StyledTableActionsCell>
                    </StyledTableRow>
                  ))}
                </StyledTableBody>
              </Table>
            </StyledTableContainer>
          )}
        </>
      )}

      {activeTab === 4 && (
        <>
          <SettingsHeaderRow
            title="Vets"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[4]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          <SimpleRecycleBinTable
            items={filteredVetItems}
            totalCount={vets.length}
            searchText={searchText}
            entityLabelSingular="vet"
            entityLabelPlural="vets"
            columnLabel="Name"
            subColumnLabel="Phone"
            onRestore={(item) => confirmRestore('vet', item.name, item.id)}
            onDelete={(item) =>
              confirmPermanentDelete('vet', item.name, item.id)
            }
            actionInProgress={actionInProgress}
            busy={busy}
          />
        </>
      )}

      {activeTab === 5 && (
        <>
          <SettingsHeaderRow
            title="Pet Types"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[5]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          <SimpleRecycleBinTable
            items={filteredPetTypeItems}
            totalCount={petTypes.length}
            searchText={searchText}
            entityLabelSingular="pet type"
            entityLabelPlural="pet types"
            columnLabel="Type"
            onRestore={(item) => confirmRestore('pet type', item.name, item.id)}
            onDelete={(item) =>
              confirmPermanentDelete('pet type', item.name, item.id)
            }
            actionInProgress={actionInProgress}
            busy={busy}
          />
        </>
      )}

      {activeTab === 6 && (
        <>
          <SettingsHeaderRow
            title="Pet Sizes"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[6]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          <SimpleRecycleBinTable
            items={filteredPetSizeItems}
            totalCount={petSizes.length}
            searchText={searchText}
            entityLabelSingular="pet size"
            entityLabelPlural="pet sizes"
            columnLabel="Size"
            subColumnLabel="Pet Type"
            onRestore={(item) => confirmRestore('pet size', item.name, item.id)}
            onDelete={(item) =>
              confirmPermanentDelete('pet size', item.name, item.id)
            }
            actionInProgress={actionInProgress}
            busy={busy}
          />
        </>
      )}

      {activeTab === 7 && (
        <>
          <SettingsHeaderRow
            title="Pet Breeds"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[7]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          <SimpleRecycleBinTable
            items={filteredPetBreedItems}
            totalCount={petBreeds.length}
            searchText={searchText}
            entityLabelSingular="pet breed"
            entityLabelPlural="pet breeds"
            columnLabel="Breed"
            subColumnLabel="Pet Type"
            onRestore={(item) =>
              confirmRestore('pet breed', item.name, item.id)
            }
            onDelete={(item) =>
              confirmPermanentDelete('pet breed', item.name, item.id)
            }
            actionInProgress={actionInProgress}
            busy={busy}
          />
        </>
      )}

      {activeTab === 8 && (
        <>
          <SettingsHeaderRow
            title="Pet Colours"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[8]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          <SimpleRecycleBinTable
            items={filteredPetColourItems}
            totalCount={petColours.length}
            searchText={searchText}
            entityLabelSingular="pet colour"
            entityLabelPlural="pet colours"
            columnLabel="Colour"
            subColumnLabel="Pet Type"
            onRestore={(item) =>
              confirmRestore('pet colour', item.name, item.id)
            }
            onDelete={(item) =>
              confirmPermanentDelete('pet colour', item.name, item.id)
            }
            actionInProgress={actionInProgress}
            busy={busy}
          />
        </>
      )}

      {activeTab === 9 && (
        <>
          <SettingsHeaderRow
            title="Kennel Sizes"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[9]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          <SimpleRecycleBinTable
            items={filteredKennelSizeItems}
            totalCount={kennelSizes.length}
            searchText={searchText}
            entityLabelSingular="kennel size"
            entityLabelPlural="kennel sizes"
            columnLabel="Name"
            onRestore={(item) =>
              confirmRestore('kennel size', item.name, item.id)
            }
            onDelete={(item) =>
              confirmPermanentDelete('kennel size', item.name, item.id)
            }
            actionInProgress={actionInProgress}
            busy={busy}
          />
        </>
      )}

      {activeTab === 10 && (
        <>
          <SettingsHeaderRow
            title="Kennel Sides"
            onSearch={setSearchText}
            onClearSearch={() => setSearchText('')}
            searchText={searchText}
            searchPlaceholder={searchPlaceholders[10]}
            rightContent={
              totalCount > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {totalCount} deleted item{totalCount !== 1 ? 's' : ''}
                </Typography>
              ) : undefined
            }
          />
          <SimpleRecycleBinTable
            items={filteredKennelSideItems}
            totalCount={kennelSides.length}
            searchText={searchText}
            entityLabelSingular="kennel side"
            entityLabelPlural="kennel sides"
            columnLabel="Name"
            onRestore={(item) =>
              confirmRestore('kennel side', item.name, item.id)
            }
            onDelete={(item) =>
              confirmPermanentDelete('kennel side', item.name, item.id)
            }
            actionInProgress={actionInProgress}
            busy={busy}
          />
        </>
      )}

      <RecycleBinConfirmDialog
        open={dialog.open}
        onClose={closeDialog}
        onConfirm={dialog.onConfirm}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
      />
    </Box>
  );
};

export default RecycleBin;
